/**
 * My working hours (Aziz 2026-09-29: "flexibility per day and time, and a monthly view
 * to block and open parts").
 *
 *   Every week   each weekday its own hours, several ranges a day (a split day)
 *   Month        tick one or many dates, then: block the day, block part of it, open it
 *                with its own hours, or put it back to the usual week
 *
 * The server resolves one rule per date (the date's own, else its weekday), and every
 * meeting time and clinic booking time is offered only inside it. Dubai time.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarClock, ChevronLeft, ChevronRight, Copy, Plus, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { fetchBookingHours, saveBookingHours, type BookingHours } from '@/lib/calls';

type Range = [string, string];
type Week = Record<string, Range[]>;
type DateRule = { off?: boolean; ranges?: Range[] };

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const wd = (d: Date) => ((d.getDay() + 6) % 7) + 1;          // ISO weekday, Monday = 1
const fmt = (r: Range[]) => r.map(([a, b]) => `${a}-${b}`).join(', ');

/** The week as per-day ranges, built from the old single window when never set. */
function weekOf(h: BookingHours): Week {
  if (h.week && Object.keys(h.week).length) return h.week as Week;
  const w: Week = {};
  for (let d = 1; d <= 7; d++) w[String(d)] = h.days.includes(d) ? [[h.start, h.end]] : [];
  return w;
}

/** Remove [a, b) from a list of ranges ("block part of a day"). */
function subtract(ranges: Range[], a: string, b: string): Range[] {
  const out: Range[] = [];
  for (const [s, e] of ranges) {
    if (b <= s || a >= e) { out.push([s, e]); continue; }
    if (s < a) out.push([s, a]);
    if (b < e) out.push([b, e]);
  }
  return out;
}

function Ranges({ value, onChange, testid }: { value: Range[]; onChange: (r: Range[]) => void; testid: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {value.map(([a, b], i) => (
        <span key={i} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-1.5 py-1">
          <input type="time" value={a} aria-label="From" className="bg-transparent text-xs text-muted-strong w-[112px] min-w-0"
            onChange={(e) => onChange(value.map((r, j) => (j === i ? [e.target.value, r[1]] : r)))} />
          <span className="text-muted text-xs">to</span>
          <input type="time" value={b} aria-label="To" className="bg-transparent text-xs text-muted-strong w-[112px] min-w-0"
            onChange={(e) => onChange(value.map((r, j) => (j === i ? [r[0], e.target.value] : r)))} />
          <button type="button" aria-label="Remove these hours" onClick={() => onChange(value.filter((_, j) => j !== i))}
            className="p-1 text-muted hover:text-red-400"><X size={12} /></button>
        </span>
      ))}
      <button type="button" onClick={() => onChange([...value, value.length ? [value[value.length - 1][1], '19:00'] : ['10:00', '18:00']])}
        className="inline-flex items-center gap-1 text-[11px] text-accent-400 hover:underline" data-testid={`${testid}-add`}>
        <Plus size={12} /> {value.length ? 'Add hours' : 'Work this day'}
      </button>
    </div>
  );
}

export default function WorkingHours() {
  const q = useQuery({ queryKey: ['booking-hours'], queryFn: fetchBookingHours });
  const [h, setH] = useState<BookingHours | null>(null);
  const [week, setWeek] = useState<Week>({});
  const [dates, setDates] = useState<Record<string, DateRule>>({});
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [custom, setCustom] = useState<Range[]>([['10:00', '14:00']]);
  const [part, setPart] = useState<Range>(['13:00', '15:00']);
  // what is saved on the server, to tell the person their blocks and hours are not saved yet:
  // a "Block part of the day" only counts once Save hours is pressed (2026-10-02)
  const [savedSig, setSavedSig] = useState('');
  const sigOf = (hh: BookingHours | null, w: Week, dd: Record<string, DateRule>) =>
    JSON.stringify({ m: hh?.minutes, n: hh?.notice_hours, b: hh?.buffer ?? 0, w, dd });
  useEffect(() => {
    if (!q.data) return;
    const w = weekOf(q.data), dd = (q.data.dates ?? {}) as Record<string, DateRule>;
    setH(q.data); setWeek(w); setDates(dd); setSavedSig(sigOf(q.data, w, dd));
  }, [q.data]);
  const dirty = !!h && !!savedSig && sigOf(h, week, dates) !== savedSig;
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => {
      const days = Object.entries(week).filter(([, r]) => r.length).map(([d]) => Number(d)).sort();
      const first = days.length ? week[String(days[0])][0] : ['10:00', '18:00'];
      return saveBookingHours({ ...h!, days: days.length ? days : [1], start: first[0], end: first[1], week, dates });
    },
    onSuccess: (r) => { setH(r); setSavedSig(sigOf(r, week, dates)); toast.success('Working hours saved'); },
    onError: (e: unknown) => toast.error((e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      ? 'Check the hours: every end after its start, and no two ranges overlapping' : 'Could not save'),
  });

  const cells = useMemo(() => {
    const first = new Date(month); const lead = wd(first) - 1;
    const n = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i + 1))];
  }, [month]);
  if (!h) return null;
  const today = iso(new Date());
  const hoursOn = (d: Date): { ranges: Range[]; kind: 'week' | 'off' | 'custom' | 'blocked' } => {
    const r = dates[iso(d)];
    if (r?.off) return { ranges: [], kind: 'blocked' };
    if (r?.ranges?.length) return { ranges: r.ranges, kind: 'custom' };
    const w = week[String(wd(d))] ?? [];
    return { ranges: w, kind: w.length ? 'week' : 'off' };
  };
  const apply = (fn: (d: string, cur: Range[]) => DateRule | null) => {
    const next = { ...dates };
    for (const k of picked) {
      const d = new Date(`${k}T12:00:00`);
      const r = fn(k, hoursOn(d).ranges);
      if (r) next[k] = r; else delete next[k];
    }
    setDates(next); setPicked(new Set());
  };
  const flip = (k: string) => { const n = new Set(picked); if (n.has(k)) n.delete(k); else n.add(k); setPicked(n); };

  return (
    <Card className="mb-4" data-testid="hours-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0"><CalendarClock size={18} /></div>
        <div className="flex-1">
          <h3 className="font-medium text-muted-strong">My working hours</h3>
          <p className="text-xs text-muted mt-0.5">Meetings, and the times clinics can pick, are offered only inside these hours, and never on your off days, meetings or shoots. Dubai time.</p>
        </div>
      </div>

      <h4 className="text-[11px] uppercase tracking-wide text-muted mb-2">Every week</h4>
      <div className="divide-y divide-slate-800 border border-slate-800 rounded-lg mb-5" data-testid="hours-week">
        {DAY_NAMES.map((n, i) => {
          const k = String(i + 1); const r = week[k] ?? [];
          return (
            <div key={k} className="flex flex-wrap items-center gap-3 px-3 py-2">
              <span className={cn('w-10 text-sm', r.length ? 'text-muted-strong' : 'text-muted')}>{n}</span>
              <div className="flex-1 min-w-[220px]">
                {r.length ? <Ranges value={r} onChange={(v) => setWeek({ ...week, [k]: v })} testid={`week-${k}`} />
                  : <span className="text-xs text-muted mr-2">Off</span>}
                {!r.length && <button type="button" onClick={() => setWeek({ ...week, [k]: [['10:00', '18:00']] })}
                  className="inline-flex items-center gap-1 text-[11px] text-accent-400 hover:underline" data-testid={`week-${k}-add`}><Plus size={12} /> Work this day</button>}
              </div>
              {r.length > 0 && (
                <button type="button" title="Use these hours on every working day" data-testid={`week-${k}-copy`}
                  onClick={() => { const w: Week = {}; for (const d of Object.keys(week)) w[d] = week[d].length ? r.map((x) => [...x] as Range) : []; setWeek(w); toast.message(`${n}'s hours copied to every working day`); }}
                  className="inline-flex items-center gap-1 text-[11px] text-muted hover:text-accent-400"><Copy size={12} /> Copy to all</button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] uppercase tracking-wide text-muted">Month: block or open days and hours</h4>
        <div className="flex items-center gap-1">
          <button type="button" aria-label="Previous month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-1.5 rounded hover:bg-white/5"><ChevronLeft size={14} /></button>
          <span className="text-sm text-muted-strong w-32 text-center" data-testid="hours-month">{month.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</span>
          <button type="button" aria-label="Next month" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-1.5 rounded hover:bg-white/5" data-testid="hours-next"><ChevronRight size={14} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">{DAY_NAMES.map((n) => <div key={n} className="text-[10px] text-muted text-center">{n}</div>)}</div>
      <div className="grid grid-cols-7 gap-1" data-testid="hours-calendar">
        {cells.map((d, i) => {
          if (!d) return <div key={`x${i}`} />;
          const k = iso(d); const past = k < today; const { ranges, kind } = hoursOn(d); const on = picked.has(k);
          return (
            <button key={k} type="button" disabled={past} onClick={() => flip(k)} data-testid="hours-day" data-date={k} data-kind={kind} aria-pressed={on}
              className={cn('min-h-[58px] rounded-md border p-1 text-left flex flex-col gap-0.5 transition',
                past && 'opacity-35 cursor-default',
                on ? 'border-accent-400 ring-2 ring-accent-400/40' : 'border-slate-800 hover:border-slate-600',
                kind === 'blocked' && 'bg-red-500/10', kind === 'custom' && 'bg-sky-500/10', kind === 'off' && 'bg-slate-900/40')}>
              <span className={cn('text-xs', k === today ? 'text-accent-400 font-semibold' : 'text-muted-strong')}>{d.getDate()}</span>
              <span className={cn('text-[9px] leading-tight break-words',
                kind === 'blocked' ? 'text-red-300' : kind === 'custom' ? 'text-sky-300' : kind === 'off' ? 'text-muted' : 'text-muted/80')}>
                {kind === 'blocked' ? 'Blocked' : kind === 'off' ? 'Off' : fmt(ranges)}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-muted">
        <span><span className="inline-block w-2 h-2 rounded-sm bg-slate-700 mr-1" />Usual week</span>
        <span><span className="inline-block w-2 h-2 rounded-sm bg-sky-500/60 mr-1" />Own hours that day</span>
        <span><span className="inline-block w-2 h-2 rounded-sm bg-red-500/60 mr-1" />Blocked</span>
      </div>

      {picked.size > 0 && (
        <div className="mt-3 rounded-lg border border-accent-400/40 bg-accent-600/5 p-3 space-y-3" data-testid="hours-actions">
          <div className="text-xs text-muted-strong">{picked.size} day{picked.size === 1 ? '' : 's'} picked</div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => apply(() => ({ off: true }))} data-testid="hours-block-day">Block the whole day</Button>
            <Button size="sm" variant="secondary" onClick={() => apply(() => null)} data-testid="hours-reset">Back to the usual week</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted">Block part:</span>
            <input type="time" value={part[0]} onChange={(e) => setPart([e.target.value, part[1]])} aria-label="Block from" className="bg-transparent border border-slate-700 rounded px-1.5 py-1 text-xs" />
            <span className="text-xs text-muted">to</span>
            <input type="time" value={part[1]} onChange={(e) => setPart([part[0], e.target.value])} aria-label="Block to" className="bg-transparent border border-slate-700 rounded px-1.5 py-1 text-xs" />
            <Button size="sm" variant="secondary" data-testid="hours-block-part"
              onClick={() => apply((_k, cur) => { const left = subtract(cur, part[0], part[1]); return left.length ? { ranges: left } : { off: true }; })}>Block this part</Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted">Open with these hours:</span>
            <Ranges value={custom} onChange={setCustom} testid="hours-custom" />
            <Button size="sm" variant="secondary" disabled={!custom.length} onClick={() => apply(() => ({ ranges: custom.map((r) => [...r] as Range) }))} data-testid="hours-open">Set these hours</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5">
        <Input label="Default meeting length (min)" type="number" min={15} max={120} step={15} value={String(h.minutes)}
          onChange={(e) => setH({ ...h, minutes: Number(e.target.value) || 30 })} />
        <Input label="Notice (hours)" type="number" min={0} max={168} value={String(h.notice_hours)}
          onChange={(e) => setH({ ...h, notice_hours: Number(e.target.value) || 0 })} />
        <Input label="Gap between meetings (min)" type="number" min={0} max={60} step={5} value={String(h.buffer ?? 0)}
          onChange={(e) => setH({ ...h, buffer: Math.max(0, Math.min(60, Number(e.target.value) || 0)) })} />
      </div>
      <div className="flex items-center justify-end gap-3 mt-3">
        {dirty && <span className="text-xs text-amber-300" data-testid="hours-unsaved">Not saved yet: your changes and blocks count once you save.</span>}
        <Button size="sm" loading={save.isPending} onClick={() => save.mutate()} data-testid="hours-save">Save hours</Button>
      </div>
    </Card>
  );
}
